// SamayPravahTab.tsx
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Waves } from "lucide-react";
import AstriaIndiaV2Shell, { SHELL_COLORS_INDIA_V2 } from "../AstriaIndiaV2Shell";
import {
  AstriaIndiaV2ResultCard,
  AstriaIndiaV2ShareRow,
  AstriaIndiaV2HighlightBadge,
  AstriaIndiaV2SignatureLine,
  IndiaV2LoadingState,
  IndiaV2ErrorState,
  IndiaV2ChatCta,
} from "../AstriaIndiaV2Card";
import { WizardProgress, WizardStep, WizardNav, WizardChips, WizardSlider } from "../AstriaIndiaV2Wizard";
import { useAstriaIndiaV2Tab } from "../useAstriaIndiaV2Tab";
import SamayPravahEnergyGraph from "../../../chat/SamayPravahEnergyGraph";
import { AstriaIndiaV2SamayData } from "../../../../contexts/ChatContext";
import { useIndiaV2Language } from "../../../../utils/India/astriaIndiaV2Language";
import { ASTRIA_INDIA_V2_COPY } from "../../../../utils/India/astriaIndiaV2Copy";
import { useIndiaV2Copy } from "../../../../utils/India/useIndiaV2Copy";

const SUBCATEGORY_NAME = "Samay Pravah";

const TOTAL_STEPS = 3;

const SamayPravahTab: React.FC = () => {
  // Which engine to call is decided ONLY by which sidebar entry the user
  // clicked ("Astria India V2" vs "Astria India V3" — see
  // Sidebar.tsx/TesterHome.tsx's "?v=3" query flag) and never changes for
  // the lifetime of this page — no in-page switch.
  const [searchParams] = useSearchParams();
  const version: "v2" | "v3" = searchParams.get("v") === "3" ? "v3" : "v2";
  const categoryName = version === "v3" ? "Astria India V3" : "Astria India V2";

  const lang = useIndiaV2Language();
  const copy = useIndiaV2Copy(lang);
  const t = copy.samay;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } = useAstriaIndiaV2Tab({
    categoryName,
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [step, setStep] = useState(1);
  const [flow, setFlow] = useState<string | null>(null);
  const [commsFrequency, setCommsFrequency] = useState(50);
  const [direction, setDirection] = useState<string | null>(null);

  const started = !!message || loading || !!error;

  const buildMessage = () => {
    const flowLabel = t.flowOptions.find((f) => f.value === flow)?.label || t.flowOptions[3].label;
    const directionLabel =
      t.directionOptions.find((d) => d.value === direction)?.label || t.directionOptions[2].label;

    return `Give me my Samay Pravah (energy flow) reading for today.
My current flow feels: ${flowLabel}.
How connected I've been with others lately: ${commsFrequency}/100.
My energy direction: ${directionLabel}.`;
  };

  const shareText = (g: AstriaIndiaV2SamayData, narrative?: string) => {
    const parts = [
      t.tabLabel,
      `Movement: ${g.movement?.type || ""}`,
      `Phase Weight: ${g.phase_weight?.type || ""}`,
      `Flow Direction: ${g.flow_direction?.type || ""}`,
    ];
    if (g.dasha) parts.push(`Dasha: ${g.dasha}`);
    if (g.sub_dasha) parts.push(`Sub-Dasha: ${g.sub_dasha}`);
    if (narrative && narrative.trim()) parts.push("", narrative.trim());
    return parts.join("\n");
  };

  const handleSubmit = () => {
    send(buildMessage());
  };

  const graph = message?.astriaIndiaV2Data as AstriaIndiaV2SamayData | undefined;

  if (started) {
    return (
      <AstriaIndiaV2Shell
        active="samay"
        lang={lang}
       
        version={version}
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#2E1F45", marginBottom: 4 }}>
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: "#8C7DA0" }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <IndiaV2LoadingState label={t.loading} />}

        {!loading && error && (
          <IndiaV2ErrorState message={error} onRetry={handleSubmit} retryLabel={shared.tryAgain} />
        )}

        {!loading && !error && graph && (
          <>
            <AstriaIndiaV2HighlightBadge text={graph.highlight} />
            <SamayPravahEnergyGraph
              graph={graph}
              guidance={message?.content}
              timestamp={message?.timestamp}
            />
            {(graph.micro_imagery || graph.astro_soft_influence || graph.clarity_point) && (
              <div
                style={{
                  marginTop: 16,
                  padding: "14px 16px",
                  borderRadius: 14,
                  background: SHELL_COLORS_INDIA_V2.section,
                  border: `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
                }}
              >
                <AstriaIndiaV2SignatureLine value={graph.micro_imagery} />
                <AstriaIndiaV2SignatureLine value={graph.astro_soft_influence} />
                <AstriaIndiaV2SignatureLine value={graph.clarity_point} emphasis />
              </div>
            )}
            <AstriaIndiaV2ShareRow
              text={shareText(graph, message?.content)}
              accent="#3A4B8C"
              labels={shared}
            />
            <button
              onClick={() => {
                reset();
                setStep(1);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: "#8C7DA0" }}
            >
              {shared.startNewReading}
            </button>
            <IndiaV2ChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !graph && message && (
          <AstriaIndiaV2ResultCard
            icon={<Waves className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
            accent={["#3A4B8C", "#1F2A54"]}
          >
            <p style={{ fontSize: 15, lineHeight: 1.75, color: "#4A3B5C", whiteSpace: "pre-wrap" }}>
              {message.content}
            </p>
          </AstriaIndiaV2ResultCard>
        )}
      </AstriaIndiaV2Shell>
    );
  }

  return (
    <AstriaIndiaV2Shell
      active="samay"
      lang={lang}
     
      version={version}
    >
      <WizardProgress
        step={step}
        total={TOTAL_STEPS}
        accent="#3A4B8C"
        stepLabel={shared.stepOf}
        percentLabel={shared.percentComplete}
      />

      {step === 1 && (
        <WizardStep stepKey="flow" question={t.step1Question}>
          <WizardChips options={t.flowOptions} value={flow} onChange={setFlow} />
          <WizardNav
            showBack={false}
            nextDisabled={!flow}
            onNext={() => setStep(2)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 2 && (
        <WizardStep stepKey="comms" question={t.step2Question}>
          <WizardSlider value={commsFrequency} onChange={setCommsFrequency} lowLabel={t.commsLow} highLabel={t.commsHigh} />
          <WizardNav
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 3 && (
        <WizardStep stepKey="direction" question={t.step3Question}>
          <WizardChips options={t.directionOptions} value={direction} onChange={setDirection} />
          <WizardNav
            onBack={() => setStep(2)}
            nextLabel={t.revealButton}
            nextDisabled={!direction}
            onNext={handleSubmit}
            backLabel={shared.back}
          />
        </WizardStep>
      )}
    </AstriaIndiaV2Shell>
  );
};

export default SamayPravahTab;
